/**
 * Observations — developmental observations per child + framework picker.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

const EYFS_DOMAINS = [
  "communication_and_language",
  "personal_social_emotional",
  "physical_development",
  "literacy",
  "mathematics",
  "understanding_the_world",
  "expressive_arts_and_design",
];
const UAE_DOMAINS = ["physical", "personal_social", "language_communication", "cognitive", "creative"];

const LEVEL_PALETTE = {
  emerging: "bg-[#FDF1D2] text-[#8C6D1F]",
  developing: "bg-[#F4E5C5] text-[#8C6D1F]",
  secure: "bg-[#E6EFE8] text-[#1F3A2A]",
  exceeding: "bg-[#E6EFE8] text-[#1F3A2A] font-semibold",
};

export default function Observations() {
  const { t } = useI18n();
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState("");
  const [observations, setObservations] = useState([]);
  const [framework, setFramework] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, f] = await Promise.all([
          api.get("/children"),
          api.get("/admin/ops/framework-config"),
        ]);
        setChildren(c.data || []);
        if (c.data?.length) setChildId(c.data[0].id);
        setFramework(f.data);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!childId) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/admin/ops/observations/${childId}`);
        setObservations(r.data || []);
      } catch {}
      setLoading(false);
    })();
  }, [childId]);

  const setActiveFramework = async (active) => {
    try {
      const r = await api.patch("/admin/ops/framework-config", { active_framework: active });
      setFramework(r.data);
      toast.success(t("ops.common.saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
  };

  const submit = async (form) => {
    try {
      await api.post(`/admin/ops/observations/${childId}`, {
        framework: framework?.active_framework || "eyfs",
        domain: form.domain,
        sub_domain: form.sub_domain || null,
        text: form.text,
        level: form.level,
        photo_urls: [],
        next_steps: form.next_steps || null,
      });
      toast.success("Observation saved.");
      setModal(null);
      const r = await api.get(`/admin/ops/observations/${childId}`);
      setObservations(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("ops.errors.generic"));
    }
  };

  const activeDomains =
    framework?.active_framework === "uae_moe"
      ? UAE_DOMAINS
      : framework?.active_framework === "custom"
      ? framework?.custom_domains || []
      : EYFS_DOMAINS;

  return (
    <main className="p-6 lg:p-10 space-y-6" data-testid="admin-observations">
      <AdminHeader
        eyebrow={t("ops.observations.eyebrow")}
        title={t("ops.observations.title")}
        action={
          <select
            value={childId}
            onChange={(e) => setChildId(e.target.value)}
            data-testid="observations-child-picker"
            className="bg-white border border-[#EEF0F4] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#1F3A2A]"
          >
            {children.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        }
      />
      <p className="text-sm text-[#6B7280] max-w-2xl">{t("ops.observations.blurb")}</p>

      {/* Framework picker */}
      <div className="flex flex-wrap items-center gap-2" data-testid="framework-picker">
        <span className="eyebrow">{t("ops.observations.framework_label")}:</span>
        {[
          ["eyfs", "framework_eyfs"],
          ["uae_moe", "framework_uae_moe"],
          ["custom", "framework_custom"],
        ].map(([key, tk]) => (
          <button
            key={key}
            onClick={() => setActiveFramework(key)}
            data-testid={`framework-${key}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              framework?.active_framework === key
                ? "bg-[#1F3A2A] text-white border-[#1F3A2A]"
                : "bg-white text-[#6B7280] border-[#EEF0F4]"
            }`}
          >
            {t(`ops.observations.${tk}`)}
          </button>
        ))}
        <button
          onClick={() => setModal({ domain: activeDomains[0] || "", sub_domain: "", text: "", level: "developing", next_steps: "" })}
          data-testid="observation-create-cta"
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]"
        >
          <Plus size={14} strokeWidth={2} />
          {t("ops.observations.add_observation")}
        </button>
      </div>

      {loading ? (
        <p className="font-serif italic text-[#525252]">{t("ops.common.loading")}</p>
      ) : observations.length === 0 ? (
        <p className="font-serif italic text-[#9CA3AF]" data-testid="observations-empty">
          {t("ops.observations.no_observations_yet")}
        </p>
      ) : (
        <ul className="space-y-3" data-testid="observations-list">
          {observations.map((o) => (
            <li
              key={o.id}
              data-testid={`observation-${o.id}`}
              className="rounded-2xl bg-white border border-[#EEF0F4] p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="eyebrow text-[#1F3A2A]">{o.domain.replace(/_/g, " ")}</span>
                    {o.sub_domain && <span className="text-[#9CA3AF]">· {o.sub_domain}</span>}
                  </div>
                  <p className="mt-2 text-sm text-[#1A1B2E] leading-relaxed">{o.text}</p>
                  {o.next_steps && (
                    <p className="mt-2 text-xs text-[#6B7280]">
                      <span className="font-medium">Next steps:</span> {o.next_steps}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-[#9CA3AF]">
                    {t("ops.observations.by_observer")} {o.observer_name} ·{" "}
                    {o.observed_at?.slice(0, 10)}
                  </p>
                </div>
                <span className={`text-[10px] tracking-[0.1em] uppercase font-semibold rounded-full px-2 py-0.5 ${LEVEL_PALETTE[o.level]}`}>
                  {t(`ops.observations.level_${o.level}`)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {modal && (
        <div role="dialog" aria-modal="true" data-testid="observation-modal" onClick={() => setModal(null)} className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl text-[#1A1B2E]">{t("ops.observations.add_observation")}</h3>
            <div className="mt-5 space-y-3">
              <label className="block">
                <span className="eyebrow">{t("ops.observations.domain_label")}</span>
                <select value={modal.domain} onChange={(e) => setModal({ ...modal, domain: e.target.value })} data-testid="obs-form-domain" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
                  {activeDomains.map((d) => (<option key={d} value={d}>{d.replace(/_/g, " ")}</option>))}
                </select>
              </label>
              <label className="block">
                <span className="eyebrow">Sub-domain (optional)</span>
                <input type="text" value={modal.sub_domain} onChange={(e) => setModal({ ...modal, sub_domain: e.target.value })} className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
              <label className="block">
                <span className="eyebrow">Observation</span>
                <textarea rows={4} value={modal.text} onChange={(e) => setModal({ ...modal, text: e.target.value })} data-testid="obs-form-text" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.observations.level_label")}</span>
                <select value={modal.level} onChange={(e) => setModal({ ...modal, level: e.target.value })} data-testid="obs-form-level" className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm bg-white">
                  {["emerging", "developing", "secure", "exceeding"].map((l) => (
                    <option key={l} value={l}>{t(`ops.observations.level_${l}`)}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="eyebrow">{t("ops.observations.next_steps_label")}</span>
                <textarea rows={2} value={modal.next_steps} onChange={(e) => setModal({ ...modal, next_steps: e.target.value })} className="mt-1 w-full px-3 py-2 border border-[#EEF0F4] rounded-lg text-sm" />
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setModal(null)} className="text-sm text-[#6B7280] hover:text-[#1A1B2E]">{t("ops.common.cancel")}</button>
              <button onClick={() => submit(modal)} data-testid="obs-form-submit" className="inline-flex items-center gap-2 rounded-full bg-[#1F3A2A] text-white text-sm font-medium px-4 py-2 hover:bg-[#132C17]">{t("ops.common.save")}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
